import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Loader2, Info, CalendarDays, RefreshCcw,
    ChevronLeft, Printer, FileSpreadsheet, Download,
    Scale, AlertCircle, CheckCircle2,
    Store
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { useToast } from '../hooks/useToast';
import { fetchTrialBalance, currencyCodeToSymbolMap, fetchStoresWhereStaffAssigned } from '../apiHelper';

// --- STYLING HELPERS ---
const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

const formatCurrency = (amount, currencyCode) => {
    const symbol = currencyCodeToSymbolMap[currencyCode] || '₦';
    return `${symbol}${new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Math.abs(amount))}`;
};

const NewTrialBalanceReport = ({ onBack }) => {
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [reportData, setReportData] = useState([]);
    const [orgName, setOrgName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 365);
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
    // const [currencyCode, setCurrencyCode] = useState('NGN');
    const [showZeroBalances, setShowZeroBalances] = useState(false);

    const [stores, setStores] = useState([]);
    const [selectedStoreId, setSelectedStoreId] = useState('');


    const initializeStores = async (parsedUser) => {
        try {
            const fetchedStores = await fetchStoresWhereStaffAssigned(parsedUser);
            const options = fetchedStores
                .filter(store => store.storetype !== 'WHS')
                .map(store => ({
                    label: store.identifier,
                    value: store.store_id,
                }));
            setStores(options);
        } catch (err) {
            console.error(err);
            showToast(err.response?.data?.detail || "Failed to load assigned stores", "error");
        }
    }

    // --- LOGIC PRESERVATION: DATA FETCHING ---
    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
            const parsedUser = JSON.parse(storedUser)
            setUser(parsedUser);
            initializeStores(parsedUser);
        } else {
            showToast('User not found. Please log in.', 'error');
            navigate('/login');
        }
    }, [navigate, showToast]);

    const loadTrialBalance = async () => {
        if (!user) return;
        setLoading(true);
        setError(null);
        try {
            const params = {
                users_id: user.users_id,
                start_date: startDate,
                end_date: endDate,
                store_id: selectedStoreId || null,
            };
            // console.log(params)
            const response = await fetchTrialBalance(params);
            console.log(response) // the source of trialbalancereport.json data
            setReportData(response.trial_balance || []);
            setOrgName(response.organization_name || '');
            // if (response.trial_balance?.[0]?.currency_code) {
            //     setCurrencyCode(response.trial_balance[0].currency_code);
            // }
        } catch (err) {
            setError(err.message || 'Failed to load trial balance.');
            showToast('Error loading report', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (user) loadTrialBalance();
    }, [user, startDate, endDate, selectedStoreId]);

    const { filteredData, totalDebits, totalCredits, currencyCode, isBalanced } = useMemo(() => {
        let debits = 0;
        let credits = 0;
        let currency = 'NGN';

        // 1. Filter out zero balances if state is false
        const filtered = showZeroBalances
            ? [...reportData]
            : reportData.filter((account) => Math.abs(account.ending_balance) > 0.01);

        // 2. Sort by account_code 
        filtered.sort((a, b) => {
            // const codeA = String(a.account_code || '');
            // const codeB = String(a.account_code || '');
            // return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
            const codeA = parseInt(a.account_code, 10) || 0;
            const codeB = parseInt(b.account_code, 10) || 0;
            return codeA - codeB;
        });

        // 3. Calculate totals based on Account Type (Normal Balances)
        filtered.forEach((item) => {
            if (item.currency_code) currency = item.currency_code;

            const type = item.account_type_code?.toLowerCase();
            const balance = item.ending_balance || 0;

            // Assets and Expenses are 'Debit Normal' accounts
            if (type === 'asset' || type === 'expense') {
                if (balance >= 0) {
                    debits += balance;
                } else {
                    credits += Math.abs(balance);
                }
            }
            // Liabilities, Equity, and Revenue are 'Credit Normal' accounts
            else if (type === 'liability' || type === 'equity' || type === 'income' || type === 'revenue'){
                if (balance >= 0) {
                    credits += balance;
                } else {
                    debits += Math.abs(balance);
                }
            }
        });

        return {
            filteredData: filtered,
            totalDebits: debits,
            totalCredits: credits,
            currencyCode: currency,
            isBalanced: Math.abs(debits - credits) < 0.01
        };
    }, [reportData, showZeroBalances]);

    return (
        <div className="min-h-screen bg-[#F9FAFB] pb-12">
            {/* Sophisticated Top Navigation */}
            <nav className="sticky top-0 z-30 bg-white/80 backdrop-blur-md border-b border-zinc-200 px-6 py-4">
                <div className="max-w-7xl mx-auto flex items-center justify-between">
                    <button
                        onClick={onBack || (() => navigate(-1))}
                        className="flex items-center text-zinc-500 hover:text-zinc-900 transition-colors font-medium text-sm gap-2"
                    >
                        <ChevronLeft size={18} /> Back to Reports
                    </button>
                    <div className="flex items-center gap-3">
                        <Button
                            variant="secondary"
                            onClick={() => setShowZeroBalances(!showZeroBalances)}
                            className={`flex items-center gap-2 px-4 border shadow-sm transition-all ${showZeroBalances
                                ? 'bg-zinc-100 border-zinc-300 text-zinc-900'
                                : 'bg-white border-zinc-200 text-zinc-500'
                                }`}
                        >
                            {showZeroBalances ? <CheckCircle2 size={16} className="text-emerald-500" /> : <AlertCircle size={16} />}
                            <span className="text-xs font-bold uppercase tracking-wider">
                                {showZeroBalances ? 'Showing All' : 'Hide Zero Balances'}
                            </span>
                        </Button>

                        <Button variant="secondary" className="flex items-center justify-center bg-white border-zinc-200 text-zinc-700 shadow-sm px-4">
                            <Printer size={16} className="mr-2" /> Print
                        </Button>
                        <Button variant="primary" className="flex items-center justify-center bg-indigo-600 hover:bg-indigo-700 shadow-sm px-4">
                            <Download size={16} className="mr-2" /> Export PDF
                        </Button>
                    </div>
                </div>
            </nav>

            <main className="max-w-7xl mx-auto px-6 mt-8 space-y-6">
                {/* Header Card */}
                <div className="bg-white rounded-2xl border border-zinc-200 p-8 shadow-sm">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div className="space-y-1">
                            <div className="flex items-center gap-2 text-indigo-600 font-semibold text-xs uppercase tracking-widest">
                                <Scale size={14} /> Financial Statement
                            </div>
                            <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">Trial Balance</h1>
                            <p className="text-zinc-500 font-medium">{orgName || 'All Organizations'}</p>
                        </div>

                        {/* Status Indicator */}
                        <div className={`flex items-center gap-3 px-4 py-2 rounded-xl border ${isBalanced
                            ? 'bg-emerald-50 border-emerald-100 text-emerald-700'
                            : 'bg-amber-50 border-amber-100 text-amber-700'
                            }`}>
                            {isBalanced ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                            <div className="text-sm">
                                <p className="font-bold leading-none">{isBalanced ? 'Balanced' : 'Out of Balance'}</p>
                                <p className="text-[11px] opacity-80">System Integrity Check</p>
                            </div>
                        </div>
                    </div>

                    {/* <div className="mt-8 pt-8 border-t border-zinc-100 flex flex-wrap items-center gap-8">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-zinc-50 rounded-lg text-zinc-400">
                                <CalendarDays size={18} />
                            </div>
                            <div>
                                <p className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider">Reporting Period</p>
                                <p className="text-sm font-semibold text-zinc-700">
                                    {startDate ? formatDate(startDate) : 'Genesis'} — {endDate ? formatDate(endDate) : 'Today'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={loadTrialBalance}
                            className="ml-auto p-2 hover:bg-zinc-100 rounded-full text-zinc-400 transition-colors"
                            title="Refresh Report"
                        >
                            <RefreshCcw size={20} className={loading ? 'animate-spin' : ''} />
                        </button>
                    </div> */}
                    <div className="flex flex-wrap items-center gap-6">
                        <div className="flex items-center gap-2 bg-zinc-50 p-2 rounded-xl border border-zinc-100 min-w-[200px]">
                            <Store size={16} className="text-zinc-400 ml-2" />
                            <select
                                value={selectedStoreId}
                                onChange={(e) => setSelectedStoreId(e.target.value)}
                                className="bg-transparent text-sm font-semibold text-zinc-700 focus:outline-none border-none w-full cursor-pointer"
                            >
                                <option value="">All Branches</option>
                                {stores.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-3 bg-zinc-50 p-2 rounded-xl border border-zinc-100">
                            <div className="flex items-center gap-2">
                                <label htmlFor="startDate" className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider px-2">From</label>
                                <input
                                    type="date"
                                    id="startDate"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="bg-transparent text-sm font-semibold text-zinc-700 focus:outline-none border-none p-0"
                                />
                            </div>
                            <div className="h-4 w-[1px] bg-zinc-200" />
                            <div className="flex items-center gap-2">
                                <label htmlFor="endDate" className="text-[10px] uppercase font-bold text-zinc-400 tracking-wider px-2">To</label>
                                <input
                                    type="date"
                                    id="endDate"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="bg-transparent text-sm font-semibold text-zinc-700 focus:outline-none border-none p-0"
                                />
                            </div>
                        </div>

                        <button
                            onClick={loadTrialBalance}
                            className="ml-auto flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-xl hover:bg-indigo-100 transition-colors"
                            title="Refresh Report"
                        >
                            <RefreshCcw size={16} className={loading ? 'animate-spin' : ''} />
                            <span className="text-xs font-bold uppercase">Update Report</span>
                        </button>
                    </div>
                </div>

                {/* Table Section */}
                <div className="bg-white rounded-2xl border border-zinc-200 shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-zinc-50/50 border-b border-zinc-200">
                                    <th className="px-8 py-4 text-[11px] font-bold text-zinc-500 uppercase tracking-widest">Account Details</th>
                                    <th className="px-6 py-4 text-[11px] font-bold text-zinc-500 uppercase tracking-widest">Type</th>
                                    <th className="px-6 py-4 text-[11px] font-bold text-zinc-500 uppercase tracking-widest text-right">Debit</th>
                                    <th className="px-6 py-4 text-[11px] font-bold text-zinc-500 uppercase tracking-widest text-right">Credit</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-zinc-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan="4" className="px-8 py-20">
                                            <div className="flex flex-col items-center justify-center gap-3">
                                                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                                                <p className="text-zinc-400 font-medium">Calculating balances...</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan="4" className="px-8 py-20 text-center text-zinc-400 italic">
                                            No transaction data found for this period.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredData.map((item, idx) => {
                                        // Re-applying the accounting logic for row-level display
                                        const type = item.account_type_code?.toLowerCase();
                                        const balance = item.ending_balance || 0;
                                        const isDebitNormal = ['asset', 'expense'].includes(type);

                                        const rowDebit = isDebitNormal ? (balance >= 0 ? balance : 0) : (balance <= 0 ? Math.abs(balance) : 0);
                                        const rowCredit = isDebitNormal ? (balance < 0 ? Math.abs(balance) : 0) : (balance > 0 ? balance : 0);

                                        return (
                                            <motion.tr
                                                key={item.account_id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                onClick={() => navigate('/reports/account-ledger-report', { state: {account_id: item.account_id}})}
                                                className="cursor-pointer hover:bg-zinc-50 transition-colors group"
                                            // ... your existing motion props
                                            >
                                                <td className="px-8 py-5">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm font-bold text-zinc-900 group-hover:text-indigo-600 transition-colors">
                                                            {item.account_name}
                                                        </span>
                                                        <span className="text-xs font-mono text-zinc-400">
                                                            {item.account_code}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-5">
                                                    <span className="px-2.5 py-1 rounded-md bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-wider">
                                                        {item.account_type}
                                                    </span>
                                                </td>
                                                <td className="px-6 py-5 text-sm font-mono text-right text-zinc-700">
                                                    {rowDebit > 0 ? formatCurrency(rowDebit, currencyCode) : '-'}
                                                </td>
                                                <td className="px-6 py-5 text-sm font-mono text-right text-zinc-700">
                                                    {rowCredit > 0 ? formatCurrency(rowCredit, currencyCode) : '-'}
                                                </td>
                                            </motion.tr>
                                        );
                                    })
                                )}
                            </tbody>
                            <tfoot className="bg-zinc-900">
                                <tr>
                                    <td colSpan="2" className="px-8 py-6 text-sm font-bold text-zinc-400 uppercase tracking-widest">
                                        Total Trial Balance
                                    </td>
                                    <td className="px-6 py-6 text-lg font-mono text-right text-white font-bold border-l border-white/10">
                                        {formatCurrency(totalDebits, currencyCode)}
                                    </td>
                                    <td className="px-6 py-6 text-lg font-mono text-right text-white font-bold border-l border-white/10">
                                        {formatCurrency(totalCredits, currencyCode)}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* Footnote */}
                <div className="flex items-center gap-2 text-zinc-400 px-2">
                    <Info size={14} />
                    <p className="text-[11px] font-medium">This report is generated based on real-time ledger entries. Balances represent the net activity for the specified period.</p>
                </div>
            </main>
        </div>
    );
};

export default NewTrialBalanceReport;